import { useCallback, useMemo } from "react";
import hash from "object-hash";

import { RepositoryVisibility } from "@protos/common/v1/common";
import { IssuesFilter as IssuesFilterProto } from "@protos/issues/v1/issues";
import { ValidationState } from "@protos/secrets/v1/secrets";
import { SECRETS_FINDINGS_PAGE } from "@shared/constants";
import { useStateParams } from "@shared/hooks";

import { useCommonIssuesFilterState } from "./useCommonIssuesFilterState";

function repositoryVisibilityToQueryParam(
  repositoryVisibility: RepositoryVisibility
): string {
  switch (repositoryVisibility) {
    case RepositoryVisibility.PRIVATE:
      return "private";
    case RepositoryVisibility.PUBLIC:
      return "public";
    case RepositoryVisibility.UNKNOWN:
      return "unknown";
    default:
      return "unspecified";
  }
}

function repositoryVisibilityFromQueryParam(
  queryParam: string
): RepositoryVisibility {
  switch (queryParam) {
    case "private":
      return RepositoryVisibility.PRIVATE;
    case "public":
      return RepositoryVisibility.PUBLIC;
    case "unknown":
      return RepositoryVisibility.UNKNOWN;
    default:
      return RepositoryVisibility.UNSPECIFIED;
  }
}

function validationStateToQueryParam(validationState: ValidationState): string {
  switch (validationState) {
    case ValidationState.CONFIRMED_VALID:
      return "confirmed_valid";
    case ValidationState.CONFIRMED_INVALID:
      return "confirmed_invalid";
    case ValidationState.VALIDATION_ERROR:
      return "validation_error";
    case ValidationState.NO_VALIDATOR:
      return "no_validator";
    default:
      return "unspecified";
  }
}

function validationStateFromQueryParam(queryParam: string): ValidationState {
  switch (queryParam) {
    case "confirmed_valid":
      return ValidationState.CONFIRMED_VALID;
    case "confirmed_invalid":
      return ValidationState.CONFIRMED_INVALID;
    case "validation_error":
      return ValidationState.VALIDATION_ERROR;
    case "no_validator":
      return ValidationState.NO_VALIDATOR;
    default:
      return ValidationState.UNSPECIFIED;
  }
}

export const useSecretsIssuesFilterState = () => {
  const commonFilters = useCommonIssuesFilterState(SECRETS_FINDINGS_PAGE);

  const [validationState, setValidationState] = useStateParams<
    IssuesFilterProto["validationStates"]
  >(
    [],
    "validation_state",
    (validationVals) =>
      validationVals.map(validationStateToQueryParam).join(","),
    (validationStr) =>
      validationStr.split(",").map(validationStateFromQueryParam),
    SECRETS_FINDINGS_PAGE
  );

  const [secretType, setSecretType] = useStateParams<
    IssuesFilterProto["secretTypes"]
  >(
    [],
    "type",
    (s) => s.join(","),
    (s) => (s ? s.split(",") : []),
    SECRETS_FINDINGS_PAGE
  );

  const [repositoryVisibility, setRepositoryVisibility] = useStateParams<
    IssuesFilterProto["repoVisibilities"]
  >(
    [],
    "repository_visibility",
    (repoVisVals) =>
      repoVisVals.map(repositoryVisibilityToQueryParam).join(","),
    (repoVisStr) =>
      repoVisStr.split(",").map(repositoryVisibilityFromQueryParam),
    SECRETS_FINDINGS_PAGE
  );

  const [excludeHistorical, setExcludeHistorical] = useStateParams<
    NonNullable<IssuesFilterProto["excludeHistorical"]>
  >(
    false,
    "exclude_historical",
    (s) => (s ? "true" : "false"),
    (s) => s === "true",
    SECRETS_FINDINGS_PAGE
  );

  /**
   * A repackaged version of this state that is consumable by the issues API endpoint.
   */
  const issuesFilterProto = useMemo<IssuesFilterProto>(
    () => ({
      ...commonFilters.issuesFilterProto,
      validationStates: validationState,
      secretTypes: secretType,
      repoVisibilities: repositoryVisibility,
      excludeHistorical: excludeHistorical,
    }),
    [
      commonFilters.issuesFilterProto,
      validationState,
      secretType,
      repositoryVisibility,
      excludeHistorical,
    ]
  );

  /**
   * A hash of all the filters in this object. Useful to detect when any filters have changed
   * without needing to understand the internal state of this hook.
   */
  const filterHash = useMemo(
    () =>
      hash([
        commonFilters.filterHash,
        validationState,
        secretType,
        repositoryVisibility,
        excludeHistorical,
      ]),
    [
      commonFilters.filterHash,
      validationState,
      secretType,
      repositoryVisibility,
      excludeHistorical,
    ]
  );

  const hasFiltersSet =
    commonFilters.hasFiltersSet ||
    validationState.length > 0 ||
    secretType.length > 0 ||
    repositoryVisibility.length > 0 ||
    excludeHistorical !== false;

  /**
   * Clears all filter fields even if they are set by default
   */
  const clearFilters = useCallback(() => {
    commonFilters.clearFilters();
    setValidationState([]);
    setSecretType([]);
    setRepositoryVisibility([]);
    setExcludeHistorical(false);
  }, [
    commonFilters,
    setValidationState,
    setSecretType,
    setRepositoryVisibility,
    setExcludeHistorical,
  ]);

  /**
   * Resets all filter fields to their default values (empty, nullish, etc).
   */
  const resetFilters = useCallback(() => {
    commonFilters.resetFilters();
    commonFilters.setRef([]);
    setValidationState([]);
    setSecretType([]);
    setRepositoryVisibility([]);
    setExcludeHistorical(false);
  }, [
    commonFilters,
    setValidationState,
    setSecretType,
    setRepositoryVisibility,
    setExcludeHistorical,
  ]);

  return useMemo(
    () => ({
      issuesFilterProto,
      repo: commonFilters.repo,
      setRepo: commonFilters.setRepo,
      repoTag: commonFilters.repoTag,
      setRepoTag: commonFilters.setRepoTag,
      repoRef: commonFilters.repoRef,
      setRepoRef: commonFilters.setRepoRef,
      team: commonFilters.team,
      setTeam: commonFilters.setTeam,
      tab: commonFilters.tab,
      severities: commonFilters.severities,
      setSeverities: commonFilters.setSeverities,
      setTab: commonFilters.setTab,
      aiVerdicts: commonFilters.aiVerdicts,
      setAiVerdicts: commonFilters.setAiVerdicts,
      aiSensitivity: commonFilters.aiSensitivity,
      setAiSensitivity: commonFilters.setAiSensitivity,
      ref: commonFilters.ref,
      setRef: commonFilters.setRef,
      primary: commonFilters.primary,
      setPrimary: commonFilters.setPrimary,
      excludeHistorical,
      setExcludeHistorical,
      validationState,
      setValidationState,
      secretType,
      setSecretType,
      repositoryVisibility,
      setRepositoryVisibility,
      clearFilters,
      resetFilters,
      filterHash,
      hasFiltersSet,
    }),
    [
      issuesFilterProto,
      commonFilters.repo,
      commonFilters.setRepo,
      commonFilters.repoTag,
      commonFilters.setRepoTag,
      commonFilters.repoRef,
      commonFilters.setRepoRef,
      commonFilters.team,
      commonFilters.setTeam,
      commonFilters.tab,
      commonFilters.severities,
      commonFilters.setSeverities,
      commonFilters.setTab,
      commonFilters.aiVerdicts,
      commonFilters.setAiVerdicts,
      commonFilters.aiSensitivity,
      commonFilters.setAiSensitivity,
      commonFilters.ref,
      commonFilters.setRef,
      commonFilters.primary,
      commonFilters.setPrimary,
      validationState,
      setValidationState,
      secretType,
      setSecretType,
      repositoryVisibility,
      setRepositoryVisibility,
      excludeHistorical,
      setExcludeHistorical,
      hasFiltersSet,
      clearFilters,
      resetFilters,
      filterHash,
    ]
  );
};
